// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import { getEffectiveServerNowMs, getRemainingClockSeconds } from "../live/platformLiveClock";
import { canUseBrowserWebSocket, getBrowserPlatformLiveWebSocketUrl } from "../live/browserPlatformLiveClient";
import { judgeLightsShownTogether } from "../live/judgeLightsShownTogether";
import type {
  JudgeLights,
  PlatformLiveAttempt,
  PlatformLiveBroadcastInfo,
  PlatformLiveServerMessage,
  PlatformLiveState,
} from "../live/platformLiveProtocol";
import type { PlatformRef } from "../types";

const offLights = (): JudgeLights => ({ J1: "off", J2: "off", J3: "off" });

export interface StreamingDisplayState {
  attempt: PlatformLiveAttempt | null;
  judgeLights: JudgeLights;
  remainingSeconds: number | null;
  // True while a verdict was given and the operator hasn't advanced yet (between lifters): displays show
  // "Esperando al siguiente atleta" instead of a generic idle state.
  waitingForNext: boolean;
  /** Veredicto en pantalla durante el reveal (solo con followPolicy "result-reveal"); null fuera de él. */
  revealedResult: "good" | "noLift" | null;
  /** Nombre del torneo y sponsors que publica el operador; se recuerdan entre atletas. */
  broadcast: PlatformLiveBroadcastInfo;
  platformRef: PlatformRef;
}

interface Props {
  platformRef: PlatformRef;
  locationSearch: string;
  followPolicy: "immediate-next" | "result-reveal";
  children: (state: StreamingDisplayState) => React.ReactNode;
}

const StreamingLiveClient = ({ platformRef, locationSearch, followPolicy, children }: Props) => {
  const [liveState, setLiveState] = React.useState<PlatformLiveState | null>(null);
  const [receivedAtMs, setReceivedAtMs] = React.useState<number | null>(null);
  const [tick, setTick] = React.useState(Date.now());
  const [broadcast, setBroadcast] = React.useState<PlatformLiveBroadcastInfo>({});
  const prefetchedPhotosRef = React.useRef<Set<string>>(new Set());

  // El nombre y los sponsors viajan con el intento actual; entre atletas se sigue mostrando lo último recibido.
  React.useEffect(() => {
    const received =
      liveState?.currentAttempt?.broadcast ??
      liveState?.lastResolvedAttempt?.attempt?.broadcast ??
      liveState?.nextAttempt?.broadcast;
    if (received === undefined) return;
    setBroadcast((current) => ({
      meetName: received.meetName ?? current.meetName,
      sponsors: received.sponsors !== undefined && received.sponsors.length > 0 ? received.sponsors : current.sponsors,
    }));
  }, [liveState?.currentAttempt, liveState?.lastResolvedAttempt, liveState?.nextAttempt]);

  // Si se cae la conexión (el server se reinicia o se duerme, un corte de red, un proxy que corta por
  // inactividad), reconecta con backoff como la pantalla de espectador. Mientras tanto deja en pantalla lo
  // último recibido: en la transmisión es mejor un cuadro quieto que uno vacío. Un rechazo es terminal.
  React.useEffect(() => {
    if (!canUseBrowserWebSocket()) return undefined;
    let disposed = false;
    let rejected = false;
    let reconnectAttempts = 0;
    let reconnectTimer: number | null = null;
    let socket: WebSocket | null = null;

    const connect = () => {
      const current = new window.WebSocket(getBrowserPlatformLiveWebSocketUrl(platformRef, locationSearch));
      socket = current;
      current.onopen = () => {
        current.send(
          JSON.stringify({ type: "spectatorJoin", day: platformRef.day, platformId: platformRef.platformId }),
        );
      };
      current.onmessage = (event) => {
        let message: PlatformLiveServerMessage;
        try {
          message = JSON.parse(String(event.data)) as PlatformLiveServerMessage;
        } catch (_error) {
          return;
        }
        if (message.type === "platformState" || (message.type === "accepted" && message.role === "spectator")) {
          if (message.type === "accepted") reconnectAttempts = 0;
          const now = Date.now();
          setLiveState(message.state);
          setReceivedAtMs(now);
          setTick(now);
        } else if (message.type === "rejected") {
          rejected = true;
          setLiveState(null);
        }
      };
      current.onclose = () => {
        if (disposed || rejected || socket !== current) return;
        const delay = Math.min(10_000, 1_000 * Math.pow(1.6, reconnectAttempts));
        reconnectAttempts += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      disposed = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [locationSearch, platformRef.day, platformRef.platformId]);

  React.useEffect(() => {
    if (
      liveState?.attemptClock == null &&
      (followPolicy !== "result-reveal" || liveState?.lastResolvedAttempt?.revealUntilMs == null)
    )
      return undefined;
    const timer = window.setInterval(() => setTick(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [followPolicy, liveState?.attemptClock, liveState?.lastResolvedAttempt]);

  // Warm the browser cache with the current + next lifter's photo so it doesn't pop-in on the
  // stream when they come up. URL-only prefetch — no disk download, no persistence.
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.Image === "undefined") return;
    const urls = [liveState?.currentAttempt?.athleteMeta?.photoUrl, liveState?.nextAttempt?.athleteMeta?.photoUrl];
    for (const url of urls) {
      if (!url || prefetchedPhotosRef.current.has(url)) continue;
      prefetchedPhotosRef.current.add(url);
      const img = new window.Image();
      img.src = url;
    }
  }, [liveState?.currentAttempt, liveState?.nextAttempt]);

  let attempt = liveState?.currentAttempt || null;
  const manualVerdictPending =
    liveState?.features?.manualVerdictConfirmation === true &&
    liveState.currentAttempt !== null &&
    liveState.lastResolvedAttempt?.attemptKey !== liveState.currentAttempt.key;
  let judgeLights = manualVerdictPending
    ? offLights()
    : attempt === null
      ? offLights()
      : liveState?.judgeLights || offLights();
  let revealedResult: "good" | "noLift" | null = null;
  if (followPolicy === "result-reveal" && liveState?.lastResolvedAttempt?.revealUntilMs !== undefined) {
    // Fixed 2s reveal window — the operator's "Siguiente" hold does not extend it.
    const effectiveNow = getEffectiveServerNowMs({ serverNowMs: liveState.serverNowMs, receivedAtMs }, tick);
    if (effectiveNow !== null && effectiveNow < liveState.lastResolvedAttempt.revealUntilMs) {
      attempt = liveState.lastResolvedAttempt.attempt;
      judgeLights = liveState.lastResolvedAttempt.judgeLights || liveState.judgeLights || offLights();
      revealedResult = liveState.lastResolvedAttempt.result;
    }
  }

  // Between lifters (a verdict was given, the operator hasn't published the next attempt yet) every streaming
  // surface shows "Esperando al siguiente atleta" — including the athlete card, which no longer jumps ahead to
  // the on-deck lifter during the hold.
  const waitingForNext =
    attempt === null && liveState != null && liveState.currentAttempt === null && liveState.lastResolvedAttempt != null;

  const remainingSeconds =
    attempt !== null && liveState?.attemptClock?.attemptKey === attempt.key
      ? getRemainingClockSeconds(
          { attemptClock: liveState.attemptClock, serverNowMs: liveState.serverNowMs, receivedAtMs },
          tick,
        )
      : null;

  return (
    <>
      {children({
        attempt,
        judgeLights: judgeLightsShownTogether(judgeLights),
        remainingSeconds,
        waitingForNext,
        revealedResult,
        broadcast,
        platformRef,
      })}
    </>
  );
};

export default StreamingLiveClient;
